import { defineNuxtPlugin } from 'nuxt/app'
import { Surreal, Value } from 'surrealdb'
import { markRaw } from 'vue'

let db: Surreal | undefined
// Shared by all callers: `db` exists before it is connected and authenticated.
let dbReady: Promise<Surreal> | undefined
let renewalHooked = false

function getDb(): Promise<Surreal> {
  dbReady ??= initDb().catch((err) => {
    dbReady = undefined // allow a retry
    throw err
  })
  return dbReady
}

async function initDb(): Promise<Surreal> {
  try {
    if (db === undefined) {
      // SDK values (RecordId, Uuid, ...) keep their state in #private fields,
      // which a reactive Proxy can't reach. Recommended Vue setup:
      // https://surrealdb.com/docs/reference/javascript/frameworks/vuejs
      db = new Surreal({
        codecOptions: {
          valueDecodeVisitor: value =>
            value instanceof Value ? markRaw(value) : value,
        },
      })
    }

    const user = await requireUser()

    const protocol = window.location.protocol === 'https:' ? 'wss://' : 'ws://'
    await db.connect(protocol + window.location.host + '/surrealdb/rpc', {
      namespace: 'witness-card',
      database: 'witness-card',
    })
    await db.ready

    await db.authenticate(user.access_token)

    // The session ends when the token it was opened with expires; hand
    // SurrealDB every renewed token before that happens.
    if (!renewalHooked) {
      renewalHooked = true;
      (await getUserManager()).events.addUserLoaded((renewed) => {
        db?.authenticate(renewed.access_token).catch(err =>
          console.error('Failed to re-authenticate to SurrealDB:', err),
        )
      })
    }

    return db
  }
  catch (err) {
    console.error('Failed to connect to SurrealDB:', err)
    throw err
  }
}

/** Runs SurrealQL; resolves to the result of every statement. Pass user input as `vars`, never in `sql`. */
async function surrealRawQuery<R extends unknown[] = unknown[]>(sql: string, vars?: Record<string, unknown>) {
  return (await getDb()).query<R>(sql, vars)
}

export default defineNuxtPlugin(() => {
  return {
    provide: {
      /** The rows of a single SELECT, as refs; `ready` settles once they are in. */
      surrealQuery: <T>(sql: string, vars?: Record<string, unknown>) => {
        const pending = ref(true)
        const data = ref<T[]>([]) as Ref<T[]>
        const error = ref<unknown>(null)

        const fetchData = async () => {
          try {
            const [rows] = await surrealRawQuery<[T[]]>(sql, vars)
            data.value = rows ?? []
          }
          catch (err) {
            error.value = err
          }
          finally {
            pending.value = false
          }
        }

        return {
          pending,
          data,
          error,
          ready: fetchData(), // Queue the fetch operation
        }
      },

      surrealRawQuery,
    },
  }
})
